import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import { RecurringTransactionFrequency, } from '../api/recurring-transaction-types';
import { TransactionType } from '../../transaction/api/transaction-types';
import { transactionDisplayTypeSchema } from '../../transaction/type/type-types';
import { useBudgetId } from '../../budget/budget-store';
import { useTimeZone } from '../../user/api/use-get-user';
import { useCreateRecurringTransactionApi } from '../api/use-create-recurring-transaction';
import { useUpdateRecurringTransactionApi } from '../api/use-update-recurring-transaction';
import { logAndThrowError } from '../../utils/errors';
export const recurringTransactionFormSchema = z
    .object({
    id: z.string().uuid(),
    budgetId: z.string().uuid(),
    accountId: z.string().uuid(),
    frequency: z.nativeEnum(RecurringTransactionFrequency),
    startDate: z.instanceof(Date),
    type: z.nativeEnum(TransactionType),
    payee: z.string().optional(),
    notes: z.string().optional(),
    bucketId: z.string().optional(),
    transferAccountId: z.string().uuid().optional(),
    inflow: z.coerce.number().optional(),
    outflow: z.coerce.number().optional(),
    displayType: transactionDisplayTypeSchema,
})
    .superRefine((data, ctx) => {
    if ((data?.inflow || 0) !== 0 && (data?.outflow || 0) !== 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Cannot have both inflow and outflow',
            path: ['outflow'],
        });
    }
    if (data.displayType.startsWith('transfer:')) {
        const [, targetAccountId] = data.displayType.split(':');
        if (data.accountId === targetAccountId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Transfer account cannot be the same as the source account',
                path: ['displayType'],
            });
        }
    }
});
export const useRecurringTransactionForm = ({ transaction, onSuccess }) => {
    const budgetId = useBudgetId();
    const timeZone = useTimeZone();
    const { mutate: mutateCreate, isPending: isCreatePending } = useCreateRecurringTransactionApi({ onSuccess });
    const { mutate: mutateUpdate, isPending: isUpdatePending } = useUpdateRecurringTransactionApi({ onSuccess });
    const defaultValues = useMemo(() => {
        // build a full backend-style RecurringTransaction by merging your overrides
        const nowIso = DateTime.now().setZone(timeZone).startOf('day').toISODate();
        const fullTx = {
            id: transaction?.id ?? uuidv7(),
            budgetId,
            accountId: transaction?.accountId ?? '',
            frequency: transaction?.frequency ?? RecurringTransactionFrequency.MONTHLY,
            startDate: transaction?.startDate ?? nowIso,
            type: transaction?.type ?? TransactionType.TRANSACTION,
            payee: transaction?.payee ?? '',
            notes: transaction?.notes ?? '',
            bucketId: transaction?.bucketId,
            transferAccountId: transaction?.transferAccountId,
            inflow: transaction?.inflow ?? 0,
            outflow: transaction?.outflow ?? 0,
        };
        // convert into form‐friendly shape (Date objects + displayType logic)
        return toFormData(fullTx, timeZone);
    }, [transaction, budgetId, timeZone]);
    const form = useForm({
        resolver: zodResolver(recurringTransactionFormSchema),
        defaultValues,
    });
    // whenever defaults change, reset the form
    useEffect(() => {
        form.reset(defaultValues);
    }, [defaultValues, form]);
    const handleSubmit = (values) => {
        const requestData = toRequestData(values, timeZone);
        // if you passed in an existing transaction with an id, update; otherwise create
        if (transaction?.id) {
            mutateUpdate(requestData);
        }
        else {
            mutateCreate(requestData);
        }
    };
    return {
        form,
        onSubmit: form.handleSubmit(handleSubmit),
        isSubmitting: isCreatePending || isUpdatePending,
    };
};
/**
 * Converts a stored RecurringTransaction into form-friendly data.
 *
 * - Converts `startDate` from a string (YYYY-MM-DD) into a JS `Date` object localized to the user’s time zone.
 * - Resolves the `displayType` string (e.g., `"transfer:accountId"` or `"bucketId"` or `"inflow"`) from bucket/transfer fields.
 * - Clears backend-only fields like `bucketId` and `transferAccountId` for form display.
 *
 * @param transaction - The recurring transaction from the backend.
 * @param timeZone - The user's configured IANA time zone (e.g. `"America/New_York"`).
 * @returns RecurringTransactionFormData with correctly typed values for form editing.
 */
function toFormData(transaction, timeZone) {
    const startDate = DateTime.fromISO(transaction.startDate, { zone: timeZone }).toJSDate();
    if (transaction.type === TransactionType.TRANSFER && transaction.transferAccountId) {
        return {
            ...transaction,
            startDate,
            displayType: `transfer:${transaction.transferAccountId}`,
            transferAccountId: undefined,
            bucketId: undefined,
        };
    }
    return {
        ...transaction,
        startDate,
        displayType: transaction.type === TransactionType.ADJUSTMENT ? 'adjustment' : transaction.bucketId || 'inflow',
        bucketId: undefined,
    };
}
/**
 * Converts form submission data into a RecurringTransactionRequest for backend persistence.
 *
 * - Converts `startDate` (JS Date) into a `YYYY-MM-DD` string in the user’s time zone.
 * - Derives `type`, `transferAccountId`, and `bucketId` from the `displayType` string.
 * - Ensures valid formatting and eliminates ambiguous cross-timezone behavior.
 *
 * @param formData - The raw form data to be normalized.
 * @param timeZone - The user's configured IANA time zone (e.g. `"America/New_York"`).
 * @returns A backend-ready RecurringTransactionRequest object with all required fields.
 */
function toRequestData(formData, timeZone) {
    const startDateStr = DateTime.fromJSDate(formData.startDate).setZone(timeZone, { keepLocalTime: true }).toISODate();
    if (!startDateStr) {
        logAndThrowError(`Invalid start date string: ${startDateStr}`);
    }
    const { displayType, ...baseData } = formData;
    if (displayType.startsWith('transfer:')) {
        const [, accountId] = displayType.split(':');
        return {
            ...baseData,
            startDate: startDateStr,
            type: TransactionType.TRANSFER,
            transferAccountId: accountId,
            bucketId: undefined,
        };
    }
    if (displayType === 'adjustment') {
        return {
            ...baseData,
            startDate: startDateStr,
            type: TransactionType.ADJUSTMENT,
            bucketId: undefined,
        };
    }
    if (displayType === 'inflow') {
        return {
            ...baseData,
            startDate: startDateStr,
            type: TransactionType.TRANSACTION,
            bucketId: undefined,
        };
    }
    return {
        ...baseData,
        startDate: startDateStr,
        type: TransactionType.TRANSACTION,
        bucketId: displayType,
    };
}
